import React, { useState, useEffect, useCallback } from 'react';
import { CalendarCheck, Trash2, ChevronUp, Tag, X, CheckSquare } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner, EmptyState,
  ConfirmDialog,
} from '../components/UI';

const STATUS_PARCELAMENTO = ['ATIVO', 'QUITADO', 'CANCELADO', 'RENEGOCIADO'];

const TIPOS_CUSTO = [
  { value: 'PARCELAMENTO_FATURA', label: 'Parcelamento de Fatura',  color: colors.primary },
  { value: 'ENCARGO',             label: 'Encargo / Juros',         color: colors.danger },
  { value: 'ANUIDADE',            label: 'Anuidade / Taxa',         color: colors.warning },
  { value: 'COMPRA_PONTOS',       label: 'Compra de Pontos',        color: '#8b5cf6' },
  { value: 'OUTRO',               label: 'Outro',                   color: colors.textMuted },
];

const tipoCustoInfo = (tipo) => TIPOS_CUSTO.find(t => t.value === tipo) || null;

export default function CriseParcelamentosPage() {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [parcelamentos,     setParcelamentos]     = useState([]);
  const [autoParcelamentos, setAutoParcelamentos] = useState([]);
  const [stats,             setStats]             = useState({});
  const [cartoes,           setCartoes]           = useState([]);
  const [titulares,         setTitulares]         = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [filtros,           setFiltros]           = useState({ search: '', status: '', mes: mesAtual, titular_id: '', cartao_id: '' });
  const [modoAuditoria,     setModoAuditoria]     = useState(false);
  const [selecionados,      setSelecionados]      = useState(new Set());
  const [classificando,     setClassificando]     = useState(false);
  const [tipoCustoSel,      setTipoCustoSel]      = useState('PARCELAMENTO_FATURA');
  const [modalOpen,         setModalOpen]         = useState(false);
  const [deleteId,          setDeleteId]          = useState(null);
  const [saving,            setSaving]            = useState(false);
  const [form, setForm] = useState({
    cartao_id: '', mes_referencia_original: '', valor_fatura_total: '', valor_nao_pago: '',
    valor_parcelado: '', num_parcelas: '', taxa_juros_mensal: '', valor_parcela: '',
    data_inicio: '', data_fim: '', observacoes: '',
  });

  // Cartões filtrados pelo titular selecionado
  const cartoesFiltrados = filtros.titular_id
    ? cartoes.filter(c => String(c.titular_id) === String(filtros.titular_id))
    : cartoes;

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filtros.status)    params.set('status', filtros.status);
    const autoParams = new URLSearchParams();
    autoParams.set('modo', modoAuditoria ? 'auditoria' : 'padrao');
    if (filtros.mes)       autoParams.set('mes', filtros.mes);
    if (filtros.titular_id) autoParams.set('titular_id', filtros.titular_id);
    if (filtros.cartao_id)  autoParams.set('cartao_id', filtros.cartao_id);
    try {
      const [parc, st, auto] = await Promise.all([
        fetch(`${API}/crise/parcelamentos?${params}`).then(r => r.json()),
        fetch(`${API}/crise/parcelamentos/stats`).then(r => r.json()),
        fetch(`${API}/crise/custos/parcelamentos?${autoParams}`).then(r => r.json()),
      ]);
      setParcelamentos(Array.isArray(parc) ? parc : []);
      setStats(st || {});
      setAutoParcelamentos(Array.isArray(auto) ? auto : []);
      setSelecionados(new Set()); // limpar seleção ao recarregar
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filtros, modoAuditoria]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${API}/cartoes`, { headers: authHeaders() }).then(r => r.json()).then(d => setCartoes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/titulares`, { headers: authHeaders() }).then(r => r.json()).then(d => setTitulares((d.data||d||[]).filter(t => t.total_cartoes > 0))).catch(() => {});
  }, []);

  // ── Seleção ──────────────────────────────────────────────────────────────
  const toggleSel = (id) => setSelecionados(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleTodos = () => {
    if (selecionados.size === autoFiltrado.length) setSelecionados(new Set());
    else setSelecionados(new Set(autoFiltrado.map(r => r.id)));
  };

  // ── Classificar em lote ──────────────────────────────────────────────────
  const classificar = async (tipoCusto) => {
    if (selecionados.size === 0) return;
    setClassificando(true);
    try {
      await fetch(`${API}/crise/custos/classificar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selecionados], tipo_custo: tipoCusto || null }),
      });
      setSelecionados(new Set());
      load();
    } catch (e) { alert('Erro: ' + e.message); }
    setClassificando(false);
  };

  // ── Modal manual ─────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm({
      cartao_id: '', mes_referencia_original: '', valor_fatura_total: '', valor_nao_pago: '',
      valor_parcelado: '', num_parcelas: '', taxa_juros_mensal: '', valor_parcela: '',
      data_inicio: '', data_fim: '', observacoes: '',
    });
    setModalOpen(true);
  };

  const calcParcela = (field, value) => {
    const newForm = { ...form, [field]: value };
    const vp = parseFloat(newForm.valor_parcelado) || 0;
    const np = parseInt(newForm.num_parcelas) || 1;
    if (vp > 0 && np > 0 && field !== 'valor_parcela') {
      newForm.valor_parcela = (vp / np).toFixed(2);
    }
    setForm(newForm);
  };

  const save = async () => {
    if (!form.cartao_id || !form.mes_referencia_original) {
      return alert('Cartão e mês de referência original são obrigatórios');
    }
    setSaving(true);
    try {
      await fetch(`${API}/crise/parcelamentos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      setModalOpen(false); load();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const avancarParcela = async (parc) => {
    try {
      const novaParcela = (parc.parcela_atual || 1) + 1;
      await fetch(`${API}/crise/parcelamentos/${parc.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parcela_atual: novaParcela, num_parcelas: parc.num_parcelas }),
      });
      load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleDelete = async () => {
    try {
      await fetch(`${API}/crise/parcelamentos/${deleteId}`, { method: 'DELETE' });
      setDeleteId(null); load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Filtro local de busca
  const autoFiltrado = autoParcelamentos.filter(r => {
    const q = (filtros.search || '').toLowerCase();
    return !q || r.nome_cartao?.toLowerCase().includes(q) || r.titular_nome?.toLowerCase().includes(q) || r.descricao?.toLowerCase().includes(q);
  });
  const totalAutoMes = autoFiltrado.reduce((s, r) => s + (r.valor || 0), 0);
  const qtdComprasUnicas = new Set(autoFiltrado.map(r => `${r.cartao_id}-${r.descricao?.split(' ')[0]}`)).size;
  const qtdClassificados = autoFiltrado.filter(r => r.tipo_custo).length;

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ paddingBottom: selecionados.size > 0 ? 80 : 0 }}>
      <PageHeader
        title="Parcelamentos"
        subtitle={modoAuditoria
          ? `${autoFiltrado.length} lançamentos — modo auditoria`
          : `${autoFiltrado.length} parcelas detectadas automaticamente`}
        onAdd={openCreate} addLabel="+ Manual" />

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { label: 'Parcelas no mês (auto)', value: fmt(totalAutoMes), color: colors.warning, sub: `${autoFiltrado.length} lançamentos` },
          { label: 'Compras únicas (aprox.)', value: qtdComprasUnicas, color: colors.info, sub: 'estimado' },
          { label: 'Classificados manualmente', value: qtdClassificados, color: colors.primary, sub: 'com tipo_custo' },
          { label: 'Parcelamentos manuais', value: stats.ativos || 0, color: '#f97316', sub: fmt(stats.valor_ativo) },
        ].map((kpi, i) => (
          <div key={i} style={{ background: colors.card, borderRadius: 12, padding: 16, border: `1px solid ${colors.border}`, flex: '1 1 160px' }}>
            <div style={{ fontSize: 11, color: colors.textMuted }}>{kpi.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            <div style={{ fontSize: 11, color: colors.textDim }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartão, titular, descrição...">
        <Select value={filtros.titular_id}
          onChange={e => setFiltros(f => ({ ...f, titular_id: e.target.value, cartao_id: '' }))}
          style={{ width: 'auto', minWidth: 160 }}>
          <option value="">Todos Titulares</option>
          {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </Select>
        <Select value={filtros.cartao_id}
          onChange={e => setFiltros(f => ({ ...f, cartao_id: e.target.value }))}
          style={{ width: 'auto', minWidth: 200 }}>
          <option value="">Todos os Cartões</option>
          {cartoesFiltrados.map(c => (
            <option key={c.id} value={c.id}>{c.nome_cartao}</option>
          ))}
        </Select>
        <Input type="month" value={filtros.mes} onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))} style={{ width: 'auto', minWidth: 160 }} />
        <button onClick={() => setFiltros(f => ({ ...f, mes: '' }))}
          style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}>
          Todos meses
        </button>
      </SearchBar>

      {/* Header da tabela automática + toggle auditoria */}
      <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <CalendarCheck size={14} color={modoAuditoria ? colors.primary : colors.warning} />
        <span style={{ fontSize: 12, fontWeight: 600, color: modoAuditoria ? colors.primary : colors.warning }}>
          {modoAuditoria
            ? 'Auditoria — todos os lançamentos de fatura (selecione e classifique)'
            : 'Detectado automaticamente das faturas — pré-filtrado parcelamentos'}
        </span>
        <button
          onClick={() => { setModoAuditoria(m => !m); setSelecionados(new Set()); }}
          style={{
            marginLeft: 'auto',
            background: modoAuditoria ? `${colors.primary}22` : `${colors.border}`,
            color: modoAuditoria ? colors.primary : colors.textMuted,
            border: `1px solid ${modoAuditoria ? colors.primary + '44' : colors.border}`,
            borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
          <CheckSquare size={12} />
          {modoAuditoria ? 'Sair da Auditoria' : 'Modo Auditoria'}
        </button>
      </div>

      {/* Tabela principal */}
      {autoFiltrado.length === 0 ? (
        <EmptyState
          message={modoAuditoria ? 'Nenhum lançamento encontrado para os filtros selecionados' : 'Nenhuma compra parcelada detectada nas faturas importadas para este período'}
          icon={CalendarCheck} />
      ) : (
        <Table>
          <thead>
            <tr>
              {modoAuditoria && (
                <Th style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={selecionados.size > 0 && selecionados.size === autoFiltrado.length}
                    ref={el => { if (el) el.indeterminate = selecionados.size > 0 && selecionados.size < autoFiltrado.length; }}
                    onChange={toggleTodos}
                    style={{ cursor: 'pointer' }} />
                </Th>
              )}
              <Th>Mês</Th><Th>Data</Th><Th>Cartão</Th><Th>Titular</Th><Th>Banco</Th>
              <Th>Descrição</Th><Th>Parcela</Th>
              {modoAuditoria && <Th>Tipo Custo</Th>}
              <Th>Valor</Th>
            </tr>
          </thead>
          <tbody>
            {autoFiltrado.map(r => {
              const sel = selecionados.has(r.id);
              const tc = tipoCustoInfo(r.tipo_custo);
              return (
                <TrHover key={r.id}
                  onClick={modoAuditoria ? () => toggleSel(r.id) : undefined}
                  style={{
                    borderLeft: r.gerenciamento_crise === 'SIM' ? '3px solid #f97316' : undefined,
                    background: sel ? `${colors.primary}18` : undefined,
                    cursor: modoAuditoria ? 'pointer' : undefined,
                  }}>
                  {modoAuditoria && (
                    <Td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={sel}
                        onChange={() => toggleSel(r.id)} style={{ cursor: 'pointer' }} />
                    </Td>
                  )}
                  <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.mes_referencia}</Td>
                  <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.data_compra || '-'}</Td>
                  <Td style={{ fontWeight: 600, fontSize: 12 }}>
                    {r.nome_cartao}
                    {r.gerenciamento_crise === 'SIM' && (
                      <span style={{ fontSize: 9, background: '#f9731622', color: '#f97316', borderRadius: 3, padding: '1px 4px', marginLeft: 4 }}>CRISE</span>
                    )}
                  </Td>
                  <Td style={{ fontSize: 12 }}>{r.titular_nome}</Td>
                  <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.banco_nome || '-'}</Td>
                  <Td style={{ fontSize: 12, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.descricao}>{r.descricao}</Td>
                  <Td>
                    {r.parcela ? (
                      <span style={{ background: `${colors.primary}22`, color: colors.primary, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        {r.parcela}
                      </span>
                    ) : <span style={{ color: colors.textDim, fontSize: 11 }}>—</span>}
                  </Td>
                  {modoAuditoria && (
                    <Td>
                      {tc ? (
                        <span style={{ background: tc.color + '22', color: tc.color, border: `1px solid ${tc.color}44`, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>
                          {tc.label}
                        </span>
                      ) : (
                        <span style={{ color: colors.textDim, fontSize: 11 }}>—</span>
                      )}
                    </Td>
                  )}
                  <Td style={{ fontWeight: 700, color: colors.warning }}>{fmt(r.valor)}</Td>
                </TrHover>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${colors.border}` }}>
              <td colSpan={modoAuditoria ? 9 : 7} style={{ padding: '8px 12px', fontWeight: 600, color: colors.textMuted, fontSize: 12 }}>
                Total parcelas no período
              </td>
              <td style={{ padding: '8px 12px', fontWeight: 700, color: colors.warning }}>{fmt(totalAutoMes)}</td>
            </tr>
          </tfoot>
        </Table>
      )}

      {/* Parcelamentos manuais */}
      {parcelamentos.length > 0 && (
        <>
          <div style={{ marginTop: 28, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>Parcelamentos manuais registrados</span>
            <Select value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))} style={{ width: 'auto', minWidth: 140, marginLeft: 'auto' }}>
              <option value="">Todos Status</option>
              {STATUS_PARCELAMENTO.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <Table>
            <thead><tr>
              <Th>Cartão</Th><Th>Mês Original</Th><Th>Valor Parcelado</Th><Th>Parcelas</Th><Th>Valor Parcela</Th><Th>Taxa</Th><Th>Status</Th><Th align="center">Ações</Th>
            </tr></thead>
            <tbody>
              {parcelamentos.map(p => {
                const progresso = p.num_parcelas > 0 ? ((p.parcela_atual - 1) / p.num_parcelas * 100).toFixed(0) : 0;
                return (
                  <TrHover key={p.id}>
                    <Td style={{ fontWeight: 600, fontSize: 12 }}>{p.nome_cartao}</Td>
                    <Td style={{ fontSize: 12 }}>{p.mes_referencia_original}</Td>
                    <Td style={{ color: colors.danger, fontWeight: 600 }}>{fmt(p.valor_parcelado)}</Td>
                    <Td>
                      <span style={{ fontWeight: 600 }}>{p.parcela_atual}/{p.num_parcelas}</span>
                      <div style={{ background: colors.bg, borderRadius: 4, overflow: 'hidden', height: 4, width: 50, marginTop: 4 }}>
                        <div style={{ height: '100%', width: `${progresso}%`, background: colors.primary, borderRadius: 4 }} />
                      </div>
                    </Td>
                    <Td>{fmt(p.valor_parcela)}</Td>
                    <Td>{p.taxa_juros_mensal ? `${p.taxa_juros_mensal}%` : '-'}</Td>
                    <Td><Badge text={p.status} color={p.status === 'ATIVO' ? colors.warning : p.status === 'QUITADO' ? colors.success : colors.textDim} /></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        {p.status === 'ATIVO' && (
                          <button onClick={() => avancarParcela(p)} style={{ background: colors.success, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
                            <ChevronUp size={12} style={{ verticalAlign: -2 }} /> Paga
                          </button>
                        )}
                        <button onClick={() => setDeleteId(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.danger }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </Td>
                  </TrHover>
                );
              })}
            </tbody>
          </Table>
        </>
      )}

      {/* ── Barra de classificação em lote (sticky bottom) ── */}
      {modoAuditoria && selecionados.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
          background: colors.card, borderTop: `2px solid ${colors.primary}`,
          padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 -4px 24px #0006',
        }}>
          <span style={{ fontWeight: 700, color: colors.primary, fontSize: 13 }}>
            <CheckSquare size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            {selecionados.size} selecionado(s)
          </span>
          <span style={{ color: colors.textDim, fontSize: 12 }}>Classificar como:</span>
          {TIPOS_CUSTO.map(tc => (
            <button key={tc.value}
              onClick={() => classificar(tc.value)}
              disabled={classificando}
              style={{
                background: tc.color + '22', color: tc.color,
                border: `1px solid ${tc.color}44`,
                borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, opacity: classificando ? 0.6 : 1,
              }}>
              <Tag size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
              {tc.label}
            </button>
          ))}
          <button
            onClick={() => classificar(null)}
            disabled={classificando}
            style={{
              background: `${colors.danger}22`, color: colors.danger,
              border: `1px solid ${colors.danger}44`,
              borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
            }}>
            Remover Classificação
          </button>
          <button
            onClick={() => setSelecionados(new Set())}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
      )}

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Novo Parcelamento de Fatura" width={750}>
        <div style={{
          background: colors.bg, borderRadius: 8, padding: 12, marginBottom: 16,
          border: `1px solid ${colors.warning}40`, fontSize: 12, color: colors.textMuted,
        }}>
          Quando não conseguir pagar a fatura total, registre aqui o parcelamento oferecido pelo banco.
          O valor da parcela é calculado automaticamente.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Cartão" required>
            <Select value={form.cartao_id} onChange={e => upd('cartao_id', e.target.value)}>
              <option value="">Selecione o cartão...</option>
              {cartoes.map(c => (
                <option key={c.id} value={c.id}>{c.nome_cartao} ({c.titular_nome})</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Mês Ref. Original" required>
            <Input type="month" value={form.mes_referencia_original} onChange={e => upd('mes_referencia_original', e.target.value)} />
          </FormField>
          <FormField label="Valor Total da Fatura (R$)">
            <Input type="number" step="0.01" value={form.valor_fatura_total} onChange={e => upd('valor_fatura_total', e.target.value)} placeholder="0.00" />
          </FormField>
          <FormField label="Valor Não Pago (R$)">
            <Input type="number" step="0.01" value={form.valor_nao_pago} onChange={e => upd('valor_nao_pago', e.target.value)} placeholder="0.00" />
          </FormField>
          <FormField label="Valor Parcelado (R$)">
            <Input type="number" step="0.01" value={form.valor_parcelado} onChange={e => calcParcela('valor_parcelado', e.target.value)} placeholder="0.00" />
          </FormField>
          <FormField label="Número de Parcelas">
            <Input type="number" value={form.num_parcelas} onChange={e => calcParcela('num_parcelas', e.target.value)} min="1" placeholder="1" />
          </FormField>
          <FormField label="Taxa Juros Mensal (%)">
            <Input type="number" step="0.01" value={form.taxa_juros_mensal} onChange={e => upd('taxa_juros_mensal', e.target.value)} placeholder="0.00" />
          </FormField>
          <FormField label="Valor da Parcela (R$)">
            <Input type="number" step="0.01" value={form.valor_parcela} onChange={e => upd('valor_parcela', e.target.value)} placeholder="Auto" />
          </FormField>
          <FormField label="Data Início">
            <Input type="date" value={form.data_inicio} onChange={e => upd('data_inicio', e.target.value)} />
          </FormField>
          <FormField label="Data Fim">
            <Input type="date" value={form.data_fim} onChange={e => upd('data_fim', e.target.value)} />
          </FormField>
        </div>
        <FormField label="Observações">
          <TextArea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} placeholder="Detalhes do parcelamento..." />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Criar Parcelamento'}</Button>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Parcelamento" message="Tem certeza que deseja excluir este parcelamento?" />
    </div>
  );
}
